import ApexCharts from 'react-apexcharts'
import { useMemo } from 'react'
import colors from 'tailwindcss/colors'

import {
  MetricsOverviewProps,
  StorageMetricsBucketProps,
  StorageMetricsProps,
} from '@/types/metricsProps'

interface GraphicsProps {
  storageMetrics: StorageMetricsProps
  metricsOverview: MetricsOverviewProps | null
}

interface SummaryCardProps {
  accentClassName: string
  label: string
  title: string
  value: string
  subtitle?: string
}

interface StorageCardProps {
  accentClassName: string
  bucket: StorageMetricsBucketProps
  title: string
}

function formatInteger(value: number) {
  return new Intl.NumberFormat('pt-BR').format(value)
}

function formatBytes(bytes: number) {
  if (bytes <= 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  )
  const value = bytes / 1024 ** exponent
  const fractionDigits = value >= 100 ? 0 : value >= 10 ? 1 : 2

  return `${new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: fractionDigits,
  }).format(value)} ${units[exponent]}`
}

function SummaryCard({
  accentClassName,
  label,
  title,
  value,
  subtitle,
}: SummaryCardProps) {
  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
      <span
        className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] ${accentClassName}`}
      >
        {label}
      </span>

      <div className="mt-5">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <p className="mt-4 text-4xl font-bold leading-none text-gray-900">
          {value}
        </p>
        {subtitle && <p className="mt-3 text-sm text-gray-500">{subtitle}</p>}
      </div>
    </div>
  )
}

function StorageCard({ accentClassName, bucket, title }: StorageCardProps) {
  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between gap-4 base:flex-col base:items-start">
        <div>
          <span
            className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] ${accentClassName}`}
          >
            Storage
          </span>
          <h3 className="mt-4 text-xl font-semibold text-gray-800">{title}</h3>
        </div>

        <div className="text-right base:text-left">
          <p className="text-sm text-gray-500">Raw size</p>
          <p className="text-base font-semibold text-gray-700">
            {formatInteger(bucket.sizeBytes)} bytes
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-1">
        <div className="rounded-2xl bg-slate-50 p-4">
          <p className="text-sm font-medium text-gray-500">Objects</p>
          <p className="mt-2 text-4xl font-bold leading-none text-gray-900">
            {formatInteger(bucket.objects)}
          </p>
        </div>

        <div className="rounded-2xl bg-slate-50 p-4">
          <p className="text-sm font-medium text-gray-500">Storage</p>
          <p className="mt-2 text-4xl font-bold leading-none text-gray-900">
            {formatBytes(bucket.sizeBytes)}
          </p>
        </div>
      </div>
    </div>
  )
}

function DetailList({
  items,
}: {
  items: Array<{ label: string; value: number }>
}) {
  return (
    <div className="mt-6 space-y-3">
      {items.map((item) => (
        <div
          className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3"
          key={item.label}
        >
          <span className="text-sm font-medium text-gray-600">
            {item.label}
          </span>
          <strong className="text-base text-gray-900">
            {formatInteger(item.value)}
          </strong>
        </div>
      ))}
    </div>
  )
}

export function Graphics({ storageMetrics, metricsOverview }: GraphicsProps) {
  const quickSummary = useMemo(() => {
    if (!metricsOverview) return []

    return [
      {
        accentClassName: 'bg-purple-100 text-purple-700',
        label: 'Catalog',
        title: 'Artists',
        value: formatInteger(metricsOverview.catalog.artists.total),
        subtitle: `${formatInteger(
          metricsOverview.catalog.artists.published,
        )} published / ${formatInteger(
          metricsOverview.catalog.artists.draft,
        )} draft`,
      },
      {
        accentClassName: 'bg-blue-100 text-blue-700',
        label: 'Catalog',
        title: 'Musics',
        value: formatInteger(metricsOverview.catalog.musics.total),
        subtitle: `${formatInteger(
          metricsOverview.catalog.musics.published,
        )} published / ${formatInteger(
          metricsOverview.catalog.musics.draft,
        )} draft`,
      },
      {
        accentClassName: 'bg-emerald-100 text-emerald-700',
        label: 'Catalog',
        title: 'Genres',
        value: formatInteger(metricsOverview.catalog.genres.total),
      },
      {
        accentClassName: 'bg-amber-100 text-amber-700',
        label: 'Catalog',
        title: 'Users',
        value: formatInteger(metricsOverview.catalog.users.total),
      },
    ]
  }, [metricsOverview])

  const artistHealthChart = useMemo(() => {
    if (!metricsOverview) {
      return {
        categories: [],
        values: [],
      }
    }

    return {
      categories: [
        'Published',
        'Draft',
        'No profile image',
        'No cover image',
        'Unclaimed',
      ],
      values: [
        metricsOverview.catalog.artists.published,
        metricsOverview.catalog.artists.draft,
        metricsOverview.catalog.artists.withoutProfileImage,
        metricsOverview.catalog.artists.withoutCoverImage,
        metricsOverview.catalog.artists.unclaimed,
      ],
    }
  }, [metricsOverview])

  const musicHealthChart = useMemo(() => {
    if (!metricsOverview) {
      return {
        categories: [],
        values: [],
      }
    }

    return {
      categories: [
        'Published',
        'Draft',
        'Playback enabled',
        'Playback disabled',
        'No audio asset',
        'No cover image',
      ],
      values: [
        metricsOverview.catalog.musics.published,
        metricsOverview.catalog.musics.draft,
        metricsOverview.catalog.musics.playbackEnabled,
        metricsOverview.catalog.musics.playbackDisabled,
        metricsOverview.catalog.musics.withoutAudioAsset,
        metricsOverview.catalog.musics.withoutCoverImage,
      ],
    }
  }, [metricsOverview])

  const issueCards = useMemo(() => {
    if (!metricsOverview) return []

    return [
      {
        accentClassName: 'bg-rose-100 text-rose-700',
        label: 'Issue',
        title: 'Artists without assets',
        value: formatInteger(metricsOverview.issues.artistsWithoutAssets),
      },
      {
        accentClassName: 'bg-orange-100 text-orange-700',
        label: 'Issue',
        title: 'Musics without assets',
        value: formatInteger(metricsOverview.issues.musicsWithoutAssets),
      },
      {
        accentClassName: 'bg-yellow-100 text-yellow-800',
        label: 'Issue',
        title: 'Published without playback',
        value: formatInteger(metricsOverview.issues.publishedWithoutPlayback),
      },
    ]
  }, [metricsOverview])

  if (!metricsOverview) return null

  return (
    <div className="mt-8 space-y-8">
      <section className="grid grid-cols-2 gap-6 base:grid-cols-1">
        <StorageCard
          accentClassName="bg-slate-200 text-slate-700"
          bucket={storageMetrics.public}
          title="Public bucket"
        />
        <StorageCard
          accentClassName="bg-gray-200 text-gray-700"
          bucket={storageMetrics.private}
          title="Private bucket"
        />
      </section>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-1">
        {quickSummary.map((item) => (
          <SummaryCard key={item.title} {...item} />
        ))}
      </section>

      <section className="grid grid-cols-2 gap-6 base:grid-cols-1">
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-800">
            Artists catalog health
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Current publishing, claim, and asset coverage for artists.
          </p>

          <div className="mt-6">
            <ApexCharts
              options={{
                chart: {
                  toolbar: {
                    show: false,
                  },
                },
                colors: [colors.purple[600]],
                dataLabels: {
                  enabled: true,
                },
                grid: {
                  borderColor: colors.gray[200],
                },
                plotOptions: {
                  bar: {
                    borderRadius: 6,
                    columnWidth: '52%',
                  },
                },
                title: {
                  text: undefined,
                },
                xaxis: {
                  categories: artistHealthChart.categories,
                  labels: {
                    rotate: -30,
                  },
                },
                yaxis: {
                  labels: {
                    formatter: (value) => formatInteger(Number(value)),
                  },
                },
                responsive: [
                  {
                    breakpoint: 900,
                    options: {
                      xaxis: {
                        labels: {
                          rotate: -45,
                          style: {
                            fontSize: '10px',
                          },
                        },
                      },
                    },
                  },
                ],
              }}
              series={[
                {
                  name: 'Artists',
                  data: artistHealthChart.values,
                },
              ]}
              type="bar"
              height={320}
            />
          </div>
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-800">
            Musics catalog health
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Publishing, playback, and asset readiness for musics.
          </p>

          <div className="mt-6">
            <ApexCharts
              options={{
                chart: {
                  toolbar: {
                    show: false,
                  },
                },
                colors: [colors.blue[600]],
                dataLabels: {
                  enabled: true,
                },
                grid: {
                  borderColor: colors.gray[200],
                },
                plotOptions: {
                  bar: {
                    borderRadius: 6,
                    columnWidth: '52%',
                  },
                },
                xaxis: {
                  categories: musicHealthChart.categories,
                  labels: {
                    rotate: -30,
                  },
                },
                yaxis: {
                  labels: {
                    formatter: (value) => formatInteger(Number(value)),
                  },
                },
                responsive: [
                  {
                    breakpoint: 900,
                    options: {
                      xaxis: {
                        labels: {
                          rotate: -45,
                          style: {
                            fontSize: '10px',
                          },
                        },
                      },
                    },
                  },
                ],
              }}
              series={[
                {
                  name: 'Musics',
                  data: musicHealthChart.values,
                },
              ]}
              type="bar"
              height={320}
            />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-[1.4fr,1fr] gap-6 base:grid-cols-1">
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-800">
            Artists detailed checks
          </h3>
          <DetailList
            items={[
              {
                label: 'Published',
                value: metricsOverview.catalog.artists.published,
              },
              {
                label: 'Draft',
                value: metricsOverview.catalog.artists.draft,
              },
              {
                label: 'Without profile image',
                value: metricsOverview.catalog.artists.withoutProfileImage,
              },
              {
                label: 'Without cover image',
                value: metricsOverview.catalog.artists.withoutCoverImage,
              },
              {
                label: 'Unclaimed',
                value: metricsOverview.catalog.artists.unclaimed,
              },
            ]}
          />
        </div>

        <div className="grid gap-4">
          {issueCards.map((item) => (
            <SummaryCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-semibold text-gray-800">
          Musics detailed checks
        </h3>
        <div className="grid grid-cols-2 gap-4 base:grid-cols-1">
          <DetailList
            items={[
              {
                label: 'Published',
                value: metricsOverview.catalog.musics.published,
              },
              {
                label: 'Draft',
                value: metricsOverview.catalog.musics.draft,
              },
              {
                label: 'Playback enabled',
                value: metricsOverview.catalog.musics.playbackEnabled,
              },
            ]}
          />

          <DetailList
            items={[
              {
                label: 'Playback disabled',
                value: metricsOverview.catalog.musics.playbackDisabled,
              },
              {
                label: 'Without audio asset',
                value: metricsOverview.catalog.musics.withoutAudioAsset,
              },
              {
                label: 'Without cover image',
                value: metricsOverview.catalog.musics.withoutCoverImage,
              },
            ]}
          />
        </div>
      </section>
    </div>
  )
}
